import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import algoliasearch from 'algoliasearch/lite';
import { categoriesListing, serviceCategoriesListing } from '../../containers/AlgoliaSearchPage/AlgoliaSearchPage.duck';
import {
  convertCategoriesList,
  transformDbCategories,
  SELECTABLE_PARENT_CATEGORY_IDS,
} from '../../util/data';
import { buildCategoryStripFacetSearchParams } from '../../containers/AlgoliaSearchPage/AlgoliaSearchPage.filters';
import { CATEGORY_STRIP_MODES, getCategoryStripIndexName } from './categoryStripConfig';

const searchClient =
  process.env.REACT_APP_ALGOLIA_APP_ID && process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY
    ? algoliasearch(
        process.env.REACT_APP_ALGOLIA_APP_ID,
        process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY,
        { _useRequestCache: true }
      )
    : null;

const buildStripItems = (mode, rawCategories, level = 'sub') => {
  if (mode === 'services' || mode === 'deals') {
    const parents = transformDbCategories(rawCategories || []) || [];
    // An item's id is matched against Algolia facet keys and written into the
    // category filter on navigation, so it must be the slug — that is what
    // listings store in publicData.categoryLevel1/2, and what a deal's
    // listingsCategories now carries via its linked service listing. Keying by
    // the row uuid matched nothing and every card read "0+ listings".
    const idOf = c => String(c.slug || c.id);

    // Parent level: one card per parent category, counts aggregated from its subcategories.
    if (level === 'parent') {
      return parents.map(parent => ({
        id: idOf(parent),
        label: parent.label,
        childIds: (parent.childCategory || []).map(idOf),
        iconUrl: parent.iconUrl,
      }));
    }
    return parents.flatMap(parent =>
      (parent.childCategory || []).map(child => ({
        id: idOf(child),
        label: child.label,
        childIds: [],
        parentId: idOf(parent),
        parentLabel: parent.label,
        iconUrl: child.iconUrl,
        parentIconUrl: parent.iconUrl,
      }))
    );
  }

  return (convertCategoriesList(rawCategories || []) || []).map(parent => ({
    id: String(parent.id),
    label: parent.name,
    childIds: (parent.subcategories || []).map(c => String(c.id)),
  }));
};

// Items that group subcategories (have childIds) sum their children's facet counts; leaf items
// use their own count. The services/deals facets are keyed by subcategory id either way.
const countForItem = (item, facetCounts, parentFacetCounts = {}, options = {}) => {
  const { mode } = options;
  // Parent-level item (has childIds).
  if (item.childIds?.length) {
    // Deals have no usable parent facet — categoryLevel1 is always
    // "general-discounts" — so a parent's count is the sum over its children.
    // The facet is keyed by the linked listing's subcategory (listingsCategories).
    //
    // This previously called getSubcategoryIdsForParent, a helper for the
    // legacy numeric taxonomy. Given a uuid parent id and DB category rows it
    // could only ever return an empty list, so every deals parent card showed
    // zero. Summing childIds works for both taxonomies.
    if (mode === 'deals') {
      return item.childIds.reduce((sum, childId) => sum + Number(facetCounts[childId] || 0), 0);
    }
    // Services: prefer the parent (categoryLevel1) facet count — accurate even when listings use
    // legacy subcategory ids. Fall back to summing subcategory counts.
    const parentCount = Number(parentFacetCounts[item.id] || 0);
    if (parentCount > 0) return parentCount;
    return item.childIds.reduce((sum, childId) => sum + Number(facetCounts[childId] || 0), 0);
  }
  return Number(facetCounts[item.id] || 0);
};

/**
 * Loads category strip items + Algolia facet counts for CategoryStrip / CategoriesPage.
 * Services & deals: level-2 items (subcategories / deal types) with direct facet counts.
 */
export const useCategoryStripData = (mode, { maxItems, facetSearchContext, level } = {}) => {
  const modeConfig = CATEGORY_STRIP_MODES[mode];
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [parentCounts, setParentCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!modeConfig) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const rawCategories =
          mode === 'services' || mode === 'deals'
            ? await dispatch(serviceCategoriesListing())
            : await dispatch(categoriesListing());

        if (cancelled) return;

        const stripItems = buildStripItems(mode, rawCategories, level);
        const facetCounts = {};
        const parentFacetCounts = {};

        const indexName = getCategoryStripIndexName(modeConfig);
        if (searchClient && indexName) {
          const facetParams = buildCategoryStripFacetSearchParams({
            facetAttribute: modeConfig.facetAttribute,
            filterParams: facetSearchContext?.filterParams,
            aroundLatLng: facetSearchContext?.aroundLatLng,
            aroundRadius: facetSearchContext?.aroundRadius,
          });
          const response = await searchClient.search([
            {
              indexName,
              params: facetParams,
            },
          ]);
          Object.assign(
            facetCounts,
            response?.results?.[0]?.facets?.[modeConfig.facetAttribute] || {}
          );
          Object.assign(
            parentFacetCounts,
            response?.results?.[0]?.facets?.['publicData.categoryLevel1'] || {}
          );
        }

        if (cancelled) return;

        const enriched = stripItems
          .map(item => ({
            id: item.id,
            label: item.label,
            imageType: item.imageType,
            parentId: item.parentId,
            parentLabel: item.parentLabel,
            iconUrl: item.iconUrl,
            parentIconUrl: item.parentIconUrl,
            count: countForItem(item, facetCounts, parentFacetCounts, { mode }),
          }))
          .sort((a, b) => {
            if (level === 'parent') {
              // DB-backed modes arrive already ordered by categories.sort_order
              // (transformDbCategories), so leave them alone. The legacy list
              // below ranks by numeric id, and Number('home-services') is NaN —
              // which made this comparator return NaN and silently give up.
              if (mode === 'services' || mode === 'deals') return 0;
              const ai = SELECTABLE_PARENT_CATEGORY_IDS.indexOf(Number(a.id));
              const bi = SELECTABLE_PARENT_CATEGORY_IDS.indexOf(Number(b.id));
              return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
            }
            return b.count - a.count;
          });

        setItems(typeof maxItems === 'number' ? enriched.slice(0, maxItems) : enriched);
        setParentCounts(parentFacetCounts);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load category strip data:', error);
          setItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [dispatch, facetSearchContext, maxItems, mode, modeConfig, level]);

  return { items, parentCounts, loading, modeConfig };
};
